// Pure logic for the spy-domain console.
//
// Named domainPool rather than spyDomains so it can't collide with the
// SpyDomains component on a case-insensitive filesystem, where the two files
// would be one.

export const DOMAIN_STATUSES = [
  { value: "active", label: "Active", hint: "New addresses are minted here" },
  { value: "draining", label: "Draining", hint: "Existing seeds keep collecting; no new ones are minted" },
  { value: "disabled", label: "Disabled", hint: "Nothing minted, and mail to it is refused" },
];

export function statusLabel(status) {
  const found = DOMAIN_STATUSES.find((s) => s.value === status);
  return found ? found.label : status || "unknown";
}

export function statusColor(status) {
  if (status === "active") return "green";
  if (status === "draining") return "orange";
  return "gray";
}

// How many customers a domain carries is what decides when it's saturated: a
// competitor who recognises one address blocks the domain, and everyone on it
// loses their seeds at once. Address count is a much weaker signal — one
// customer with twenty watches is far less exposure than twenty customers with
// one each.
export const SATURATION_WARN_ORGS = 15;
export const SATURATION_DRAIN_ORGS = 30;

export function saturation(domain) {
  const orgs = (domain && domain.organization_count) || 0;
  if (domain && domain.status !== "active") {
    return { level: "none", advice: "" };
  }
  if (orgs >= SATURATION_DRAIN_ORGS) {
    return {
      level: "high",
      advice: `${orgs} customers have seeds here. Add a new domain and set this one to draining — one filter rule would cost all of them.`,
    };
  }
  if (orgs >= SATURATION_WARN_ORGS) {
    return {
      level: "watch",
      advice: `${orgs} customers are on this domain. Worth lining up a replacement before it gets much busier.`,
    };
  }
  return { level: "none", advice: "" };
}

// A domain nothing has ever arrived at is either brand new or its DNS is
// wrong, and those want different responses.
export function deliveryWarning(domain) {
  if (!domain || domain.status === "disabled") return "";
  const addresses = domain.address_count || 0;
  if (addresses > 0 && !domain.last_message_date) {
    return "Addresses have been planted here but nothing has ever arrived. Check the MX record and the SES receipt rule.";
  }
  return "";
}

// Deleting is only offered when it's safe; otherwise draining is the move.
export function canDelete(domain) {
  return !!domain && (domain.address_count || 0) === 0;
}

export function poolSummary(domains) {
  const list = Array.isArray(domains) ? domains : [];
  return {
    total: list.length,
    active: list.filter((d) => d.status === "active").length,
    addresses: list.reduce((sum, d) => sum + (d.address_count || 0), 0),
    messages: list.reduce((sum, d) => sum + (d.message_count || 0), 0),
  };
}

// The three things that must be true for mail to a seed to reach the app, in
// the order they're usually finished.
export function setupChecks(domain) {
  const d = domain || {};
  return [
    { key: "rule", done: d.rule_status === "ok", label: "Amazon's receipt rule covers it (the app adds it)" },
    { key: "mx", done: !!d.mx_found, label: "MX record points at Amazon" },
    { key: "amazon", done: d.ses_status === "Success", label: "Amazon has verified the domain" },
  ];
}

// setupState sums up a domain's setup for the table: ready, ready but with
// something to fix, or still being set up.
export function setupState(domain) {
  const warnings = ((domain && domain.dns_warnings) || []).length;
  if (domain && domain.ready_date) {
    return warnings ? { label: `Ready, ${warnings} to fix`, color: "orange" } : { label: "Ready", color: "green" };
  }
  const left = setupChecks(domain).filter((c) => !c.done).length;
  return { label: `Setting up: ${left} to go`, color: warnings ? "red" : "arcoblue" };
}
